import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import { Play, Pause, Clock } from 'lucide-react';
import { formatVideoTime, getVideoPlayableRange } from '../utils/videoPlayerUtils';

/**
 * Shared video player for brand submission / revision review modals.
 * - Duration from the loaded <video> element (not stale stored metadata)
 * - Seek bar for scrubbing
 * - Play/pause overlay (always visible when paused)
 */
const SubmissionVideoPlayer = forwardRef(function SubmissionVideoPlayer(
  { video, videoUrl, showSafeZone = false, badge = null, className = '', onTimeUpdate },
  ref
) {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);

  const getRange = useCallback(() => {
    const el = videoRef.current;
    return getVideoPlayableRange(video, el?.duration || videoDuration || 0);
  }, [video, videoDuration]);

  useImperativeHandle(ref, () => ({
    seekTo(seconds) {
      const el = videoRef.current;
      if (!el) return;
      const { rangeStart, rangeEnd } = getRange();
      const clamped = Math.min(Math.max(seconds, rangeStart), rangeEnd);
      el.currentTime = clamped;
      setCurrentTime(clamped);
    },
    getCurrentTime() {
      return videoRef.current?.currentTime ?? 0;
    },
    isPlaying() {
      return isPlaying;
    },
  }));

  // Reset when switching videos
  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setVideoDuration(0);
  }, [videoUrl, video?.id]);

  const clampToRange = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    const { rangeStart, rangeEnd } = getRange();
    if (el.currentTime < rangeStart) {
      el.currentTime = rangeStart;
    } else if (el.currentTime > rangeEnd) {
      el.currentTime = rangeStart;
      if (isPlaying) el.play().catch(() => {});
    }
    setCurrentTime(el.currentTime);
  }, [getRange, isPlaying]);

  const handleTimeUpdate = () => {
    const el = videoRef.current;
    if (!el) return;
    const { rangeStart, rangeEnd } = getRange();
    let t = el.currentTime;
    if (t < rangeStart) {
      el.currentTime = rangeStart;
      t = rangeStart;
    } else if (t >= rangeEnd) {
      el.currentTime = rangeStart;
      t = rangeStart;
      if (isPlaying && el.paused) el.play().catch(() => {});
    }
    setCurrentTime(t);
    onTimeUpdate?.(t);
  };

  const togglePlayPause = () => {
    const el = videoRef.current;
    if (!el) return;
    const { rangeStart, rangeEnd } = getRange();
    if (el.currentTime < rangeStart || el.currentTime > rangeEnd) {
      el.currentTime = rangeStart;
    }
    if (isPlaying) {
      el.pause();
      setIsPlaying(false);
    } else {
      el.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  };

  const handleLoadedMetadata = () => {
    const el = videoRef.current;
    if (!el) return;
    setVideoDuration(el.duration || 0);
    const { rangeStart } = getVideoPlayableRange(video, el.duration || 0);
    if (el.readyState >= 2) {
      el.currentTime = rangeStart;
      setCurrentTime(rangeStart);
    }
  };

  const handleSeek = (e) => {
    const el = videoRef.current;
    if (!el) return;
    const { rangeStart, rangeEnd } = getRange();
    const playable = Math.max(rangeEnd - rangeStart, 0);
    if (playable <= 0) return;
    const pct = parseFloat(e.target.value) / 100;
    el.currentTime = rangeStart + pct * playable;
    setCurrentTime(el.currentTime);
  };

  const { rangeStart, rangeEnd } = getVideoPlayableRange(video, videoDuration);
  const playableDuration = Math.max(rangeEnd - rangeStart, 0);
  const seekPercent =
    playableDuration > 0 ? ((currentTime - rangeStart) / playableDuration) * 100 : 0;
  const displayDuration = playableDuration > 0 ? playableDuration : videoDuration;

  return (
    <div className={`brand-feedback-video-player ${className}`.trim()}>
      <div className="brand-feedback-video-container">
        {badge}
        <video
          key={videoUrl}
          ref={videoRef}
          src={videoUrl}
          className="brand-feedback-video"
          poster={video?.thumbnailUrl}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onSeeked={clampToRange}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => setIsPlaying(false)}
          onClick={togglePlayPause}
        />
        {showSafeZone && <div className="brand-feedback-video-safe-zone" />}
        <div
          className={`brand-feedback-video-overlay${!isPlaying ? ' brand-feedback-video-overlay--paused' : ''}`}
        >
          <button
            type="button"
            className="brand-feedback-play-btn"
            onClick={(e) => {
              e.stopPropagation();
              togglePlayPause();
            }}
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? <Pause size={24} /> : <Play size={24} />}
          </button>
        </div>
      </div>

      <div className="brand-feedback-video-seek-row">
        <input
          type="range"
          className="brand-feedback-video-seek-input"
          min={0}
          max={100}
          step={0.1}
          value={seekPercent}
          onChange={handleSeek}
          aria-label="Video progress"
        />
      </div>

      <div className="brand-feedback-video-info">
        <div className="brand-feedback-current-time">
          <Clock size={16} />
          <span>
            {formatVideoTime(currentTime)} / {formatVideoTime(displayDuration)}
          </span>
        </div>
      </div>
    </div>
  );
});

export default SubmissionVideoPlayer;
